package com.rainbowclubs.rainbow.presentation.more

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.common.bottomNavBar.BottomNavBar
import com.rainbowclubs.rainbow.presentation.common.bottomNavBar.BottomNavItem
import com.rainbowclubs.rainbow.presentation.theme.*
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun MoreScreen(
    clubId: String,
    viewModel: MoreViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    navigateTo: (MoreViewModel.MoreNavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    MoreScaffold(
        screenState = screenState,
        onRetryClick = viewModel::fetchUser,
        onHomeClick = viewModel::navigateToHome,
        onClubClick = viewModel::navigateToClub,
        onMessagesClick = viewModel::navigateToMessages,
        onChangeClubClick = viewModel::navigateToSelectClub,
        onLogoutClick = viewModel::logOut,
    )
}

@Composable
private fun MoreScaffold(
    screenState: ScreenState<MoreUiState>,
    onRetryClick: () -> Unit,
    onHomeClick: () -> Unit,
    onClubClick: () -> Unit,
    onMessagesClick: () -> Unit,
    onChangeClubClick: () -> Unit,
    onLogoutClick: () -> Unit,
) {
    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
        bottomBar = {
            BottomNavBar(
                selectedItem = BottomNavItem.More,
                onHomeClick = onHomeClick,
                onClubClick = onClubClick,
                onMessagesClick = onMessagesClick,
                onMoreClick = { },
            )
        },
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = onRetryClick,
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> MoreScreenContent(
                    uiState = state.data,
                    onChangeClubClick = onChangeClubClick,
                    onLogoutClick = onLogoutClick,
                )
            }
        }
    }
}

@Composable
private fun MoreScreenContent(
    uiState: MoreUiState,
    onChangeClubClick: () -> Unit,
    onLogoutClick: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
    ) {

        Spacer(modifier = Modifier.height(24.dp))

        UserQrCard(uiState = uiState)

        Spacer(modifier = Modifier.height(24.dp))

        Column(
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            MoreOptionTile(
                title = stringResource(Res.string.change_club),
                icon = Res.drawable.ic_clubs,
                iconContentDescription = stringResource(Res.string.change_club_icon_cd),
                containerColor = MaterialTheme.colorScheme.yellow_background,
                accentColor = MaterialTheme.colorScheme.primary,
                onClick = onChangeClubClick,
            )
            MoreOptionTile(
                title = stringResource(Res.string.logout),
                icon = Res.drawable.ic_logout,
                iconContentDescription = stringResource(Res.string.logout_icon_cd),
                containerColor = MaterialTheme.colorScheme.errorContainer,
                accentColor = MaterialTheme.colorScheme.error,
                onClick = onLogoutClick,
            )
        }

        Spacer(modifier = Modifier.height(16.dp))
    }
}

@Composable
private fun UserQrCard(
    uiState: MoreUiState,
    modifier: Modifier = Modifier,
) {
    val shape = RoundedCornerShape(24.dp)

    Box(
        modifier = modifier
            .fillMaxWidth()
            .clip(shape)
            .background(MaterialTheme.colorScheme.rainbowGradient, shape)
            .padding(2.dp),
    ) {
        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(22.dp),
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.blueBackground,
            ),
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp, vertical = 28.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Image(
                    painter = painterResource(Res.drawable.rainbow_clubs),
                    contentDescription = stringResource(Res.string.logo_image_cd),
                    modifier = Modifier.size(88.dp),
                    contentScale = ContentScale.Fit,
                )

                Spacer(modifier = Modifier.height(12.dp))

                Text(
                    text = uiState.userName,
                    color = MaterialTheme.colorScheme.onBackground,
                    style = MaterialTheme.typography.titleLarge,
                )

                Spacer(modifier = Modifier.height(4.dp))

                Text(
                    text = stringResource(Res.string.member_id),
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                )

                Spacer(modifier = Modifier.height(20.dp))

                Surface(
                    shape = RoundedCornerShape(20.dp),
                    color = MaterialTheme.colorScheme.surface,
                ) {
                    val encodedUserId = uiState.userId
                    UserQrCode(
                        encodedUserId = encodedUserId,
                        modifier = Modifier
                            .padding(14.dp)
                            .size(172.dp),
                    )
                }
            }
        }
    }
}

@Composable
private fun MoreOptionTile(
    title: String,
    icon: DrawableResource,
    iconContentDescription: String,
    containerColor: Color,
    accentColor: Color,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(20.dp))
            .clickable(onClick = onClick),
        shape = RoundedCornerShape(20.dp),
        color = containerColor,
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(44.dp)
                    .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(14.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(icon),
                    contentDescription = iconContentDescription,
                    modifier = Modifier.size(24.dp),
                    tint = accentColor,
                )
            }

            Spacer(modifier = Modifier.width(14.dp))

            Text(
                modifier = Modifier.weight(1f),
                text = title,
                color = MaterialTheme.colorScheme.onSurface,
                style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
            )

            Box(
                modifier = Modifier
                    .size(28.dp)
                    .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(14.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_chevron_right),
                    contentDescription = stringResource(Res.string.chevron_icon_cd),
                    modifier = Modifier.size(20.dp),
                    tint = accentColor,
                )
            }
        }
    }
}

@Composable
private fun UserQrCode(
    encodedUserId: String,
    modifier: Modifier = Modifier,
) {
    val qrDescription = stringResource(Res.string.user_qr_code_cd)
    val qrCode = remember(encodedUserId) { QrCode.encode(encodedUserId.ifBlank { "unknown" }) }
    val darkColor = MaterialTheme.colorScheme.onSurface
    val lightColor = MaterialTheme.colorScheme.surface

    Canvas(
        modifier = modifier.semantics {
            contentDescription = qrDescription
        },
    ) {
        drawRect(color = lightColor)

        val qrSize = qrCode.size + QrQuietZone * 2
        val cellSize = size.minDimension / qrSize
        val contentSize = cellSize * qrSize
        val left = (size.width - contentSize) / 2f
        val top = (size.height - contentSize) / 2f

        for (y in 0 until qrCode.size) {
            for (x in 0 until qrCode.size) {
                if (!qrCode.isDark(x, y)) continue
                drawRect(
                    color = darkColor,
                    topLeft = Offset(
                        left + (x + QrQuietZone) * cellSize,
                        top + (y + QrQuietZone) * cellSize,
                    ),
                    size = Size(cellSize, cellSize),
                )
            }
        }
    }
}

private const val QrQuietZone = 4

private class QrCode private constructor(
    val size: Int,
    private val modules: List<Boolean>,
) {
    fun isDark(x: Int, y: Int): Boolean = modules[y * size + x]

    companion object {
        private const val MinVersion = 1
        private const val MaxVersion = 5
        private const val FinderSize = 7
        private const val MaskPattern = 0
        private const val ByteMode = 0x4
        private const val FormatMask = 0x5412
        private const val FormatGenerator = 0x537
        private val dataCodewords = intArrayOf(0, 19, 34, 55, 80, 108)
        private val errorCorrectionCodewords = intArrayOf(0, 7, 10, 15, 20, 26)
        private val alignmentCenters = arrayOf(
            intArrayOf(),
            intArrayOf(),
            intArrayOf(6, 18),
            intArrayOf(6, 22),
            intArrayOf(6, 26),
            intArrayOf(6, 30),
        )

        fun encode(text: String): QrCode {
            val data = text.toQrPayloadBytes()
            val version = (MinVersion..MaxVersion).firstOrNull {
                data.size <= maxByteLength(it)
            } ?: error("User id is too long to encode as a QR code")
            val dataWords = encodeDataCodewords(data, dataCodewords[version])
            val codewords = dataWords + reedSolomonRemainder(
                dataWords,
                errorCorrectionCodewords[version],
            )
            return QrMatrix(version).apply {
                drawFunctionPatterns()
                drawCodewords(codewords)
                drawFormatBits()
            }.toQrCode()
        }

        private fun maxByteLength(version: Int): Int {
            val capacityBits = dataCodewords[version] * 8
            return (capacityBits - 4 - 8) / 8
        }

        private fun String.toQrPayloadBytes(): ByteArray = ByteArray(length) { index ->
            this[index].code.toByte()
        }

        private fun encodeDataCodewords(data: ByteArray, capacity: Int): List<Int> {
            val bits = mutableListOf<Boolean>()

            fun append(value: Int, length: Int) {
                for (i in length - 1 downTo 0) {
                    bits += ((value ushr i) and 1) != 0
                }
            }

            append(ByteMode, 4)
            append(data.size, 8)
            data.forEach { append(it.toInt() and 0xFF, 8) }

            repeat(minOf(4, capacity * 8 - bits.size)) {
                bits += false
            }
            while (bits.size % 8 != 0) {
                bits += false
            }

            val result = bits.chunked(8).map { byteBits ->
                byteBits.fold(0) { acc, bit -> (acc shl 1) or if (bit) 1 else 0 }
            }.toMutableList()
            var padWord = 0xEC
            while (result.size < capacity) {
                result += padWord
                padWord = padWord xor 0xEC xor 0x11
            }
            return result
        }

        private fun reedSolomonRemainder(data: List<Int>, degree: Int): List<Int> {
            val generator = reedSolomonGenerator(degree)
            val result = MutableList(degree) { 0 }
            data.forEach { dataByte ->
                val factor = dataByte xor result.removeAt(0)
                result += 0
                generator.forEachIndexed { index, coefficient ->
                    result[index] = result[index] xor finiteFieldMultiply(coefficient, factor)
                }
            }
            return result
        }

        private fun reedSolomonGenerator(degree: Int): List<Int> {
            val result = MutableList(degree) { 0 }
            result[degree - 1] = 1
            var root = 1
            repeat(degree) {
                for (i in 0 until degree) {
                    result[i] = finiteFieldMultiply(result[i], root)
                    if (i + 1 < degree) {
                        result[i] = result[i] xor result[i + 1]
                    }
                }
                root = finiteFieldMultiply(root, 0x02)
            }
            return result
        }

        private fun finiteFieldMultiply(x: Int, y: Int): Int {
            var left = x
            var right = y
            var result = 0
            while (right != 0) {
                if (right and 1 != 0) {
                    result = result xor left
                }
                left = (left shl 1) xor if (left and 0x80 != 0) 0x11D else 0
                right = right ushr 1
            }
            return result and 0xFF
        }

        private fun getFormatBits(): Int {
            val data = (1 shl 3) or MaskPattern
            var remainder = data shl 10
            for (i in 14 downTo 10) {
                if ((remainder ushr i) and 1 != 0) {
                    remainder = remainder xor (FormatGenerator shl (i - 10))
                }
            }
            return ((data shl 10) or remainder) xor FormatMask
        }

        private fun mask(x: Int, y: Int): Boolean = (x + y) % 2 == 0

        private class QrMatrix(private val version: Int) {
            private val size = version * 4 + 17
            private val modules = MutableList(size * size) { false }
            private val functionModules = MutableList(size * size) { false }

            private fun index(x: Int, y: Int) = y * size + x

            private fun inBounds(x: Int, y: Int): Boolean = x in 0 until size && y in 0 until size

            private fun setModule(x: Int, y: Int, isDark: Boolean, isFunction: Boolean = false) {
                if (!inBounds(x, y)) return
                modules[index(x, y)] = isDark
                if (isFunction) {
                    functionModules[index(x, y)] = true
                }
            }

            fun drawFunctionPatterns() {
                drawFinder(0, 0)
                drawFinder(size - FinderSize, 0)
                drawFinder(0, size - FinderSize)
                drawTimingPatterns()
                drawAlignmentPatterns()
                reserveFormatAreas()
                setModule(8, size - 8, true, isFunction = true)
            }

            private fun drawFinder(startX: Int, startY: Int) {
                for (y in -1..FinderSize) {
                    for (x in -1..FinderSize) {
                        val absoluteX = startX + x
                        val absoluteY = startY + y
                        val isDark = when {
                            x !in 0 until FinderSize || y !in 0 until FinderSize -> false
                            x == 0 || x == FinderSize - 1 || y == 0 || y == FinderSize - 1 -> true
                            x in 2..4 && y in 2..4 -> true
                            else -> false
                        }
                        setModule(absoluteX, absoluteY, isDark, isFunction = true)
                    }
                }
            }

            private fun drawTimingPatterns() {
                for (i in 0 until size) {
                    if (!functionModules[index(6, i)]) {
                        setModule(6, i, i % 2 == 0, isFunction = true)
                    }
                    if (!functionModules[index(i, 6)]) {
                        setModule(i, 6, i % 2 == 0, isFunction = true)
                    }
                }
            }

            private fun drawAlignmentPatterns() {
                val centers = alignmentCenters[version]
                centers.forEach { centerY ->
                    centers.forEach { centerX ->
                        if (functionModules[index(centerX, centerY)]) return@forEach
                        for (y in -2..2) {
                            for (x in -2..2) {
                                val isDark = maxOf(kotlin.math.abs(x), kotlin.math.abs(y)) != 1
                                setModule(centerX + x, centerY + y, isDark, isFunction = true)
                            }
                        }
                    }
                }
            }

            private fun reserveFormatAreas() {
                for (i in 0..5) setModule(8, i, false, isFunction = true)
                setModule(8, 7, false, isFunction = true)
                setModule(8, 8, false, isFunction = true)
                setModule(7, 8, false, isFunction = true)
                for (i in 9..14) {
                    setModule(14 - i, 8, false, isFunction = true)
                }
                for (i in 0..7) {
                    setModule(size - 1 - i, 8, false, isFunction = true)
                }
                for (i in 8..14) {
                    setModule(8, size - 15 + i, false, isFunction = true)
                }
            }

            fun drawCodewords(codewords: List<Int>) {
                var bitIndex = 0
                var upward = true
                var right = size - 1
                while (right > 0) {
                    if (right == 6) right--
                    for (vertical in 0 until size) {
                        val y = if (upward) size - 1 - vertical else vertical
                        for (x in right downTo right - 1) {
                            if (functionModules[index(x, y)]) continue

                            val isDark = if (bitIndex < codewords.size * 8) {
                                val codeword = codewords[bitIndex / 8]
                                ((codeword ushr (7 - bitIndex % 8)) and 1) != 0
                            } else {
                                false
                            }
                            bitIndex++
                            setModule(x, y, isDark xor mask(x, y))
                        }
                    }
                    upward = !upward
                    right -= 2
                }
            }

            fun drawFormatBits() {
                val bits = getFormatBits()
                for (i in 0..5) setModule(8, i, ((bits ushr i) and 1) != 0, isFunction = true)
                setModule(8, 7, ((bits ushr 6) and 1) != 0, isFunction = true)
                setModule(8, 8, ((bits ushr 7) and 1) != 0, isFunction = true)
                setModule(7, 8, ((bits ushr 8) and 1) != 0, isFunction = true)
                for (i in 9..14) {
                    setModule(14 - i, 8, ((bits ushr i) and 1) != 0, isFunction = true)
                }
                for (i in 0..7) {
                    setModule(size - 1 - i, 8, ((bits ushr i) and 1) != 0, isFunction = true)
                }
                for (i in 8..14) {
                    setModule(8, size - 15 + i, ((bits ushr i) and 1) != 0, isFunction = true)
                }
                setModule(8, size - 8, true, isFunction = true)
            }

            fun toQrCode(): QrCode = QrCode(size, modules)
        }
    }
}

@Preview
@Composable
private fun MoreScreenContentPreview() {
    RainbowClubTheme {
        MoreScreenContent(
            uiState = MoreUiState(
                userName = "John Doe",
                userId = "12345",
            ),
            onChangeClubClick = {},
            onLogoutClick = {},
        )
    }
}
